<template>
    <div v-dark class="quiz-scores">
        <div v-dark class="quiz-scores__head">
            <div v-dark class="quiz-scores__title">
                <Icon type="barChart" />
                <h2>Quiz Scores</h2>
            </div>
        </div>
        <template v-if="!isShowingTextDescription">
            <div
                ref="chartContainerRef"
                tabindex="-1"
                class="quiz-scores__chart-container"
                role="region"
                aria-label="Quiz scores graph, summary text description below"
            >
                <Bar
                    :key="`${quizScores.length}_${isDarkMode}_${chartHeight}`"
                    type="percent"
                    class="quiz-scores__bar"
                    size="small"
                    :bar-color="barColor"
                    :is-dark-mode="isDarkMode"
                    :hide-average-line="true"
                    :data="quizScores"
                    description="Graph of quiz scores by quiz"
                />
                <div class="quiz-scores__bar-x-labels">
                    <div v-dark class="quiz-scores__bar-x-label">
                        {{ chartXLabels.left }}
                    </div>
                    <div v-dark class="quiz-scores__bar-x-label">
                        {{ chartXLabels.right }}
                    </div>
                </div>
            </div>
        </template>
        <template v-else-if="isShowingTextDescription && orderedQuizzes.length > 0">
            <div
                ref="quizScoresTextDescriptionRef"
                class="quiz-scores__text-description"
                tabindex="-1"
            >
                {{ textDescriptionDateRange }},
                {{ textDescriptionScoreSummary }}.
            </div>
        </template>
        <template v-else-if="isShowingTextDescription && !orderedQuizzes.length">
            <div class="quiz-scores__text-description">
                You haven't taken a quiz yet. Quiz scores will display here when you take a quiz.
            </div>
        </template>
        <div
            v-dark
            tabindex="0"
            class="quiz-scores__text-toggle"
            :class="{
                'quiz-scores__text-toggle--active': isShowingTextDescription,
            }"
            role="switch"
            aria-label="Text Description"
            :aria-checked="isShowingTextDescription ? 'true' : 'false'"
            @focus="isShowingTextDescriptionTooltip = true"
            @blur="isShowingTextDescriptionTooltip = false"
            @mouseenter="isShowingTextDescriptionTooltip = true"
            @mouseleave="isShowingTextDescriptionTooltip = false"
            @click="toggleTextDescription"
            @keydown.enter="toggleTextDescription"
            @mousedown.prevent
        >
            <Tooltip
                v-if="isShowingTextDescriptionTooltip"
                :theme="breakpoint === 'black-bear' ? 'rightalign' : undefined"
                :is-dark-mode="isDarkMode"
            >
                Text Description
            </Tooltip>
            <Icon class="quiz-scores__text-toggle-icon" type="text" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import UIKit from '@pocketprep/ui-kit'
import BrandColors from '@pocketprep/ui-kit/pocketprep-export.module.scss'
import { userModule } from '@/store/user/module'
import { quizModule } from '@/store/quiz/module'
import { getScoreRange, months } from '@/utils'
import { screenModule } from '@/store/screen/module'
import { ResizeObserver as ResizeObserverPonyFill } from 'resize-observer'

const {
    Icon,
    Bar,
    Tooltip,
} = UIKit

const isShowingTextDescriptionTooltip = ref(false)
const isShowingTextDescription = ref(false)
const quizScoresTextDescriptionRef = ref<HTMLElement | null>(null)
const chartContainerRef = ref<HTMLElement | null>(null)
const chartHeight = ref(0)

const resizeObserver = ref<ResizeObserver | null>(null)

watch(chartContainerRef, (el, previousEl) => {
    if (previousEl) {
        resizeObserver.value?.unobserve(previousEl)
    }
    if (el) {
        resizeObserver.value?.observe(el)
    }
})

onBeforeUnmount(() => {
    resizeObserver.value?.disconnect()
})

const orderedQuizzes = computed(() => {
    return [ ...quizModule.state.quizzes ]
        .filter((q) => {
            return (q.mode !== -1 && q.mode !== 5)
        })
        .sort((a, b) => {
            return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        })
})

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const quizScores = computed(() => {
    if (!orderedQuizzes.value.length) {
        return [
            { y: 26 },
            { y: 31 },
            { y: 36 },
            { y: 41 },
            { y: 47 },
            { y: 51 },
            { y: 57 },
            { y: 67 },
            { y: 75 },
            { y: 82 },
            { y: 89 },
            { y: 97 },
        ]
    }

    return orderedQuizzes.value
        .reduce((acc, q) => {
            if (!q.answers.length) {
                return acc
            }

            const dateObj = new Date(q.createdAt)
            const tooltipDate = formatTooltipDate(dateObj)
            const score = Math.round((q.correctCount / (q.correctCount + q.incorrectCount)) * 100)
            const hoverColor = getBarHoverColor(score)
            if (hoverColor) {
                acc.push({
                    y: score,
                    states: {
                        hover: {
                            color: hoverColor,
                            borderColor: hoverColor,
                        },
                    },
                    info: tooltipDate,
                })
            }

            return acc
        }, [] as { y: number; info: string; states: { hover: { color: string; borderColor: string } } }[])
})

const maxMinScoreInfo = computed(() => {
    return orderedQuizzes.value.reduce((acc, q) => {
        const score = Math.round((q.correctCount / (q.correctCount + q.incorrectCount)) * 100)
        if (!acc.max || score > acc.max.score) {
            acc.max = {
                score,
                date: new Date(q.createdAt),
            }
        }
        if (!acc.min || score < acc.min.score) {
            acc.min = {
                score,
                date: new Date(q.createdAt),
            }
        }
        return acc
    }, { max: null, min: null } as Record<'max' | 'min', { score: number; date: Date } | null>)
})

const firstQuizDate = computed(() => {
    return orderedQuizzes.value.length
        ? new Date(orderedQuizzes.value[0].createdAt)
        : null
})

const lastQuizDate = computed(() => {
    return orderedQuizzes.value.length
        ? new Date(orderedQuizzes.value[orderedQuizzes.value.length - 1].createdAt)
        : null
})

const textDescriptionDateRange = computed(() => {
    if (!firstQuizDate.value || !lastQuizDate.value) {
        return ''
    }

    if (numDaysStudying.value === 1) {
        return `On ${fullDate(firstQuizDate.value)}`
    }

    return `From ${fullDate(firstQuizDate.value)} through ${fullDate(lastQuizDate.value)}`
})

const textDescriptionScoreSummary = computed(() => {
    if (!maxMinScoreInfo.value.max || !maxMinScoreInfo.value.min) {
        return ''
    }

    if (orderedQuizzes.value.length === 1) {
        return `your quiz score was ${maxMinScoreInfo.value.max.score}%`
    }

    const uniqueScores = new Set(quizScores.value.map((s) => s.y))
    if (uniqueScores.size === 1 || (numDaysStudying.value === 1)) {
        return `your highest quiz score was ${
            maxMinScoreInfo.value.max.score
        }% and your lowest quiz score was ${
            maxMinScoreInfo.value.min.score
        }%`
    }

    return `your highest quiz score was ${
        maxMinScoreInfo.value.max.score
    }% on ${
        fullDate(maxMinScoreInfo.value.max.date)
    }, and your lowest quiz score was ${
        maxMinScoreInfo.value.min.score
    }% on ${
        fullDate(maxMinScoreInfo.value.min.date)
    }`
})

const numDaysStudying = computed(() => {
    if (!firstQuizDate.value || !lastQuizDate.value) {
        return 0
    }

    const startOfFirstQuizDate = new Date(
        firstQuizDate.value.getFullYear(),
        firstQuizDate.value.getMonth(),
        firstQuizDate.value.getDate()
    )

    const numDays = Math.ceil((lastQuizDate.value.getTime() - startOfFirstQuizDate.getTime()) / 86400000)
    return numDays
})

const shouldShowLabelYears = computed(() => {
    const firstDateYear = firstQuizDate.value?.getFullYear()

    if (firstDateYear && firstDateYear !== new Date().getFullYear()) {
        return true
    } else {
        return false
    }
})

const chartXLabels = computed(() => {
    const firstDate = firstQuizDate.value
    const lastDate = lastQuizDate.value

    if (!firstDate || !lastDate) {
        return {
            left: 'Oldest',
            right: 'Most Recent',
        }
    }

    return {
        left: `${months[firstDate.getMonth()]?.slice(0, 3)} ${firstDate.getDate()}${
            shouldShowLabelYears.value ? `, ${firstDate.getFullYear()}` : ''
        }`,
        right: `${months[lastDate.getMonth()]?.slice(0, 3)} ${lastDate.getDate()}${
            shouldShowLabelYears.value ? `, ${lastDate.getFullYear()}` : ''
        }`,
    }
})

const breakpoint = computed(() => {
    return screenModule.getters.getBreakpoint()
})

const barColor = computed(() => {
    return orderedQuizzes.value.length
        ? undefined
        : isDarkMode.value
            ? breakpoint.value === 'black-bear'
                ? BrandColors.charcoal
                : BrandColors.moonlitOcean
            : BrandColors.grayProgressBackground
})

onMounted(() => {
    userModule.actions.fetchUserData()

    // The chart fills whatever height the column gives it, and Highcharts only measures its container when it
    // renders, so we want to re-render whenever the container height changes
    const updateChartHeight = () => {
        chartHeight.value = chartContainerRef.value?.clientHeight || 0
    }
    resizeObserver.value = window.ResizeObserver
        ? new ResizeObserver(updateChartHeight)
        : new ResizeObserverPonyFill(updateChartHeight)
    if (chartContainerRef.value) {
        resizeObserver.value.observe(chartContainerRef.value)
    }
})

const getBarHoverColor = (score: number) => {
    const scoreRange = getScoreRange(score)
    return !isDarkMode.value
        ? scoreRange === 'low'
            ? BrandColors.tomato
            : scoreRange === 'medium'
                ? BrandColors.cheddar
                : BrandColors.field
        : scoreRange === 'low'
            ? BrandColors.brandRed
            : scoreRange === 'medium'
                ? BrandColors.bananaBread
                : BrandColors.jungleGreen
}

const formatTooltipDate = (date: Date) => {
    const day = ('0' + String(date.getDate())).slice(-2)
    const month = ('0' + String(date.getMonth() + 1)).slice(-2)
    const year = date.getFullYear()
    return `${month}.${day}.${year}`
}

const fullDate = (date: Date) => {
    return `${
        months[date.getMonth()]
    } ${
        date.getDate()
    }, ${
        date.getFullYear()
    }`
}

const toggleTextDescription = () => {
    isShowingTextDescription.value = !isShowingTextDescription.value

    if (isShowingTextDescription.value) {
        setTimeout(() => {
            const textDescriptionEl = quizScoresTextDescriptionRef.value
            if (textDescriptionEl) {
                textDescriptionEl.focus()
            }
        }, 0)
    }
}
</script>

<style lang="scss" scoped>
.quiz-scores {
    background: $white;
    border: 1px solid $gray-divider;
    border-radius: 6px;
    padding: 11px 14px 81px;
    box-sizing: border-box;
    min-height: 409px;
    position: relative;
    display: flex;
    flex-direction: column;

    @include breakpoint(black-bear) {
        min-height: 319px;
    }

    &--dark {
        background: $brand-black;
        border-color: $slate;
        color: $fog;
    }

    &__head {
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-shrink: 0;
        box-sizing: border-box;
        height: 32px;
        padding-bottom: 7px;
        border-bottom: 2px solid $gray-divider;
        padding-left: 8px;
        margin-bottom: 23px;

        &--dark {
            padding-bottom: 8px;
            border-bottom-width: 2px;
            border-color: $ash;
        }
    }

    &__title {
        display: flex;
        align-items: center;

        svg {
            height: 22px;
            margin-right: 8px;
            color: $slate;
        }

        h2 {
            font-size: 16px;
            line-height: 22px;
            font-weight: 600;
            margin: 0;
        }

        &--dark svg {
            color: $fog;
        }
    }

    &__chart-container {
        outline: none;
        position: relative;
        flex: 1;
        min-height: 196px;

        @include breakpoint(black-bear) {
            min-height: 170px;
        }
    }

    &__bar {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;

        // Highcharts gives the percentages on the right its own default font, and sets it inline
        :deep(.highcharts-axis-labels),
        :deep(.highcharts-axis-labels *) {
            font-family: 'Avenir Next', Muli, sans-serif !important;
        }
    }

    &__bar-x-labels {
        display: flex;
        justify-content: space-between;
        position: absolute;
        top: calc(100% + 2px);
        left: 0;
        right: 0;
        padding: 0 46px 0 6px;
        box-sizing: border-box;
    }

    &__bar-x-label {
        font-size: 12px;
        line-height: 16px;
        letter-spacing: 0.3px;
        color: $slate-01;

        &--dark {
            opacity: 0.8;
            color: $white;
        }
    }

    &__text-description {
        box-sizing: border-box;
        padding: 0 8px;
        flex: 1;
        line-height: 26px;
        outline: none;
        font-weight: 400;
    }

    &__text-toggle {
        display: inline-flex;
        cursor: pointer;
        color: $brand-blue;
        border-radius: 6px;
        outline: none;
        position: absolute;
        left: 9px;
        bottom: 9px;

        &:hover {
            color: $brand-black;
        }

        &:focus {
            &::after {
                content: '';
                position: absolute;
                left: -2px;
                top: -2px;
                width: calc(100% + 4px);
                height: calc(100% + 4px);
                box-sizing: border-box;
                border: 1px solid $brand-blue;
                border-radius: 8px;
            }
        }

        &--dark {
            color: $banana-bread;

            &:hover {
                color: $butterscotch;
            }

            &:focus {
                &::after {
                    border-color: $butterscotch;
                }
            }
        }

        &--active {
            background-color: $brand-blue;
            color: $white;

            &:hover {
                background-color: $brand-black;
                color: $white;
            }

            &:focus {
                &::after {
                    left: -3px;
                    top: -3px;
                    width: calc(100% + 6px);
                    height: calc(100% + 6px);
                    border-radius: 9px;
                }
            }

            &--dark {
                background-color: $banana-bread;
                color: $brand-black;

                &:hover {
                    background-color: $butterscotch;
                    color: $brand-black;
                }
            }
        }

        .uikit-tooltip {
            position: absolute;
            left: 50%;
            top: calc(-100% - 4px);

            @include breakpoint(brown-bear) {
                top: calc(-100% + 4px);
            }

            @include breakpoint(black-bear) {
                top: calc(-100% + 8px);
            }
        }
    }

    &__text-toggle-icon {
        width: 40px;
        height: 40px;
    }
}
</style>
